import { Fragment, type ReactNode } from 'react'

/**
 * The assistant's replies, drawn as the light Markdown its instructions ask
 * for: paragraphs, "- " and "1." lists (one level of nesting), "### "
 * headings, **bold**, *italic*, `code`, and http(s) links.
 *
 * Builds elements, never HTML, so nothing the model writes can become
 * markup. Anything it does not recognise stays as the words it was. Partial
 * text renders too, because the reply streams in. Emails and SMS drafts are
 * not drawn through this — the customer receives them as plain text.
 */

type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'heading'; text: string }
  | { kind: 'list'; ordered: boolean; items: ListItem[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'rule' }

interface ListItem {
  text: string
  children: string[]
}

const BULLET = /^(\s*)[-*•]\s+(.*)$/
const NUMBERED = /^(\s*)\d+[.)]\s+(.*)$/
const HEADING = /^\s*#{1,6}\s+(.*?)\s*#*\s*$/
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/
const QUOTE = /^\s*>\s?(.*)$/

export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = []
  let paragraph: string[] | null = null
  let list: Extract<Block, { kind: 'list' }> | null = null
  let quote: string[] | null = null

  const close = () => {
    paragraph = null
    list = null
    quote = null
  }

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (raw.trim() === '') {
      close()
      continue
    }

    if (RULE.test(raw)) {
      close()
      blocks.push({ kind: 'rule' })
      continue
    }

    const heading = HEADING.exec(raw)
    if (heading) {
      close()
      blocks.push({ kind: 'heading', text: heading[1] ?? '' })
      continue
    }

    const item = BULLET.exec(raw) ?? NUMBERED.exec(raw)
    if (item) {
      const ordered = !BULLET.test(raw)
      const indent = (item[1] ?? '').replace(/\t/g, '  ').length
      const words = item[2] ?? ''
      const current = list as Extract<Block, { kind: 'list' }> | null
      const last = current?.items.at(-1)
      if (current && last && indent >= 2) {
        last.children.push(words)
        continue
      }
      if (!current || current.ordered !== ordered) {
        paragraph = null
        quote = null
        list = { kind: 'list', ordered, items: [] }
        blocks.push(list)
      }
      ;(list as Extract<Block, { kind: 'list' }>).items.push({ text: words, children: [] })
      continue
    }

    const quoted = QUOTE.exec(raw)
    if (quoted) {
      if (!quote) {
        close()
        quote = []
        blocks.push({ kind: 'quote', lines: quote })
      }
      ;(quote as string[]).push(quoted[1] ?? '')
      continue
    }

    // A wrapped line under a list item belongs to that item.
    const openList = list as Extract<Block, { kind: 'list' }> | null
    const lastItem = openList?.items.at(-1)
    if (openList && lastItem && /^\s+\S/.test(raw)) {
      if (lastItem.children.length) lastItem.children[lastItem.children.length - 1] += ` ${raw.trim()}`
      else lastItem.text += ` ${raw.trim()}`
      continue
    }

    if (!paragraph) {
      close()
      paragraph = []
      blocks.push({ kind: 'paragraph', lines: paragraph })
    }
    ;(paragraph as string[]).push(raw.trim())
  }

  return blocks
}

const INLINE =
  /(`[^`\n]+`)|(\*\*[^*\n]+?\*\*|__[^_\n]+?__)|(\*[^*\s][^*\n]*?\*)|\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g

export function inline(text: string, keyPrefix = 'i'): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let index = 0
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0
    if (at > last) out.push(text.slice(last, at))
    const key = `${keyPrefix}${index++}`
    const [whole, code, bold, italic, linkText, linkHref, bare] = match
    if (code) {
      out.push(
        <code key={key} className="rounded bg-slate-100 px-1 py-px font-mono text-[12.5px] text-ink-800">
          {code.slice(1, -1)}
        </code>,
      )
    } else if (bold) {
      out.push(
        <strong key={key} className="font-semibold text-ink-900">
          {inline(bold.slice(2, -2), `${key}-`)}
        </strong>,
      )
    } else if (italic) {
      out.push(<em key={key}>{inline(italic.slice(1, -1), `${key}-`)}</em>)
    } else if (linkText && linkHref) {
      out.push(<Link key={key} href={linkHref}>{linkText}</Link>)
    } else if (bare) {
      out.push(<Link key={key} href={bare}>{bare}</Link>)
    } else {
      out.push(whole)
    }
    last = at + whole.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

function Link({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-violet-700 underline decoration-violet-300 underline-offset-2 hover:decoration-violet-700"
    >
      {children}
    </a>
  )
}

function Lines({ lines, prefix }: { lines: string[]; prefix: string }) {
  return lines.map((line, index) => (
    <Fragment key={index}>
      {index > 0 ? <br /> : null}
      {inline(line, `${prefix}${index}-`)}
    </Fragment>
  ))
}

function List({ ordered, items, prefix }: { ordered: boolean; items: ListItem[]; prefix: string }) {
  const Tag = ordered ? 'ol' : 'ul'
  return (
    <Tag
      className={
        ordered
          ? 'flex list-decimal flex-col gap-1 pl-5 marker:text-ink-400'
          : 'flex list-disc flex-col gap-1 pl-5 marker:text-violet-400'
      }
    >
      {items.map((item, index) => (
        <li key={index} className="pl-0.5">
          {inline(item.text, `${prefix}${index}-`)}
          {item.children.length ? (
            <ul className="mt-1 flex list-[circle] flex-col gap-1 pl-5 marker:text-violet-300">
              {item.children.map((child, childIndex) => (
                <li key={childIndex}>{inline(child, `${prefix}${index}-${childIndex}-`)}</li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </Tag>
  )
}

export function Markdown({ text, lead }: { text: string; lead?: ReactNode }) {
  const blocks = parseBlocks(text)
  const leadsParagraph = lead != null && blocks[0]?.kind === 'paragraph'

  return (
    <div className="flex min-w-0 flex-col gap-2.5 text-[13.5px] leading-relaxed break-words text-ink-800">
      {lead != null && !leadsParagraph ? <p>{lead}</p> : null}
      {blocks.map((block, index) => {
        const key = `b${index}-`
        switch (block.kind) {
          case 'paragraph':
            return (
              <p key={key}>
                {index === 0 && leadsParagraph ? lead : null}
                <Lines lines={block.lines} prefix={key} />
              </p>
            )
          case 'heading':
            return (
              <h4 key={key} className="pt-1 text-[13.5px] font-semibold text-ink-900">
                {inline(block.text, key)}
              </h4>
            )
          case 'list':
            return <List key={key} ordered={block.ordered} items={block.items} prefix={key} />
          case 'quote':
            return (
              <blockquote key={key} className="border-l-2 border-violet-200 pl-3 text-ink-600">
                <Lines lines={block.lines} prefix={key} />
              </blockquote>
            )
          case 'rule':
            return <hr key={key} className="border-ink-200" />
        }
      })}
    </div>
  )
}
